/** AutoClan page. */
import React, { useEffect, useMemo, useRef, useState, type CSSProperties, type PointerEvent as ReactPointerEvent } from "react"
import { createPortal } from "react-dom"
import { useNavigate } from "react-router-dom"
import { autoclanApi } from "../api/endpoints"
import { useTelegram } from "../hooks/useTelegram"
import { useI18n } from "../hooks/useI18n"
import { autoclanUi } from "../i18n/autoclanUi"
import { useAuthStore } from "../store/authStore"
import type { Autoclan, AutoclanDetail, AutoclanMember, AutoclanInvitePreview } from "../types/api"
import { GarageBackdrop } from "./GarageBackdrop"
import affiliateIcon from "../assets/figma-garage-part1/autoclan/affiliate.svg"
import successAndroid from "../assets/figma-garage-lifecycle/success-android.svg"
import successIos from "../assets/figma-garage-lifecycle/success-ios.svg"
import "./AutoclanPage.figma.css"
import { useGarageDisplayVehicle } from "../hooks/useGarageDisplayVehicle"

function autoclanMaskStyle(src: string): CSSProperties {
  return { "--bbf-autoclan-icon-mask": `url("${src}")` } as CSSProperties
}

const CLAN_NAME_RE = /^[A-Za-zА-Яа-яЁё0-9 _-]{0,20}$/

function normalizeClanNameInput(value: string) {
  return value.replace(/[^A-Za-zА-Яа-яЁё0-9 _-]/g, "").slice(0, 20)
}

function extractInviteCode(startParam?: string) {
  if (!startParam) return ""
  return startParam.startsWith("clan_") ? startParam.slice(5) : ""
}

function isValidPublicTelegramChatLink(value: string) {
  const raw = value.trim()
  if (!raw) return false

  let username = ""
  if (raw.startsWith("@")) {
    username = raw.slice(1)
  } else {
    const candidate = raw.includes("://") ? raw : `https://${raw.replace(/^\/+/, "")}`
    try {
      const url = new URL(candidate)
      const host = url.hostname.toLowerCase().replace(/\.$/, "")
      if (!["t.me", "www.t.me", "telegram.me", "www.telegram.me"].includes(host)) return false
      if (!["http:", "https:"].includes(url.protocol)) return false
      const parts = url.pathname.split("/").filter(Boolean)
      if (parts.length !== 1) return false
      if (parts[0].startsWith("+") || parts[0].toLowerCase() === "joinchat") return false
      username = parts[0]
    } catch {
      return false
    }
  }

  return /^[A-Za-z][A-Za-z0-9_]{4,31}$/.test(username)
}

function EmbeddedAutoclanNotice({ notice, isIOS }: { notice: "created" | "left"; isIOS: boolean }) {
  const { locale, direction } = useI18n()
  const node = (
    <div className={`bbf-autoclan-notice ${notice}`} role="status">
      <div className="bbf-autoclan-notice-content">
        {notice === "created" && <img src={isIOS ? successIos : successAndroid} alt="" />}
        <span dir={direction}>{autoclanUi(locale, notice === "created" ? "created" : "left")}</span>
      </div>
    </div>
  )
  const host = typeof document !== "undefined" ? document.querySelector(".bbf-garage-screen") : null
  return host ? createPortal(node, host) : node
}

export function AutoclanPage({ embedded = false, onClose }: { embedded?: boolean; onClose?: () => void } = {}) {
  const navigate = useNavigate()
  const { tg, hapticImpact, hapticNotification, showAlert, showConfirm, shareUrl, startParam } = useTelegram()
  const { locale, direction, t } = useI18n()
  const c = (key: Parameters<typeof autoclanUi>[1], vars?: Record<string, string | number>) => autoclanUi(locale, key, vars)
  const runtimeError = (error: any, fallbackKey: Parameters<typeof autoclanUi>[1]) => locale === "ru" && error?.message ? error.message : c(fallbackKey)
  const bootstrapClans = useAuthStore((state) => state.autoclans)
  const bootstrapDetail = useAuthStore((state) => state.autoclanDetail)
  const bootstrapReady = useAuthStore((state) => state.autoclanReady)
  const refreshAutoclanState = useAuthStore((state) => state.refreshAutoclanState)

  const [clans, setClans] = useState<Autoclan[]>(() => bootstrapClans)
  const [selectedClanId, setSelectedClanId] = useState<number | null>(() => bootstrapDetail?.id ?? bootstrapClans[0]?.id ?? null)
  const [detail, setDetail] = useState<AutoclanDetail | null>(() => bootstrapDetail)
  const [isLoading, setIsLoading] = useState(!bootstrapReady)
  const [isDetailLoading, setIsDetailLoading] = useState(false)
  const [isCreating, setIsCreating] = useState(false)
  const [joinCode, setJoinCode] = useState(extractInviteCode(startParam))
  const [isJoining, setIsJoining] = useState(false)
  const [newClanName, setNewClanName] = useState("")
  const [chatUrlInput, setChatUrlInput] = useState("")
  const [isChatEditorOpen, setIsChatEditorOpen] = useState(false)
  const [isSavingChat, setIsSavingChat] = useState(false)
  const [invitePreview, setInvitePreview] = useState<AutoclanInvitePreview | null>(null)
  const [isInvitePreviewLoading, setIsInvitePreviewLoading] = useState(false)
  const [notice, setNotice] = useState<"created" | "left" | null>(null)
  const dragStartYRef = useRef<number | null>(null)
  const dragOffsetRef = useRef(0)
  const [dragOffset, setDragOffset] = useState(0)
  const [isDragging, setIsDragging] = useState(false)
  const isIOS = ["ios", "macos"].includes((tg?.platform || "").toLowerCase())
  const chatCopy = locale === "ru"
    ? {
        chat: "Чат",
        invite: "Пригласить",
        title: "Чат клана",
        placeholder: "https://t.me/...",
        save: "Сохранить",
        notReady: "Чат клана ещё не создан",
        unavailable: "Чат клана недоступен. Владелец может привязать публичную ссылку заново",
        saveError: "Не удалось привязать чат",
        openError: "Не удалось открыть чат",
      }
    : {
        chat: "Chat",
        invite: c("invite"),
        title: "Clan chat",
        placeholder: "https://t.me/...",
        save: "Save",
        notReady: "The clan chat has not been created yet",
        unavailable: "The clan chat is unavailable. The owner can link a public URL again",
        saveError: "Could not link the chat",
        openError: "Could not open the chat",
      }

  const chatUrlHasValue = chatUrlInput.trim().length > 0
  const chatUrlIsValid = isValidPublicTelegramChatLink(chatUrlInput)
  const chatUrlIsInvalid = chatUrlHasValue && !chatUrlIsValid

  const selectedClan = useMemo(
    () => clans.find((clan) => clan.id === selectedClanId) || null,
    [clans, selectedClanId]
  )

  const hasActiveClan = clans.length > 0
  const hasInviteLink = Boolean(joinCode.trim())
  const isInvitePreviewMode = hasInviteLink && !hasActiveClan && !detail
  const inviteMembers = isInvitePreviewMode ? (invitePreview?.members || []) : []
  const displayedMembers = detail?.members || inviteMembers
  const displayedClanName = detail?.name || selectedClan?.name || (isInvitePreviewMode ? invitePreview?.clan_name : null)
  const inviteJoinDisabled = isJoining || isInvitePreviewLoading || !invitePreview
  const inviteJoinLabel = locale === "ru" ? "Вступить в клан" : c("join")
  const { vehicle: previewVehicle, isLoading: isPreviewVehicleLoading } = useGarageDisplayVehicle(!embedded)
  const close = () => { if (onClose) onClose(); else navigate(-1) }

  const setSheetDragOffset = (value: number) => {
    const next = Math.max(0, value)
    dragOffsetRef.current = next
    setDragOffset(next)
  }

  const handleGrabberPointerDown = (event: ReactPointerEvent<HTMLElement>) => {
    if (!embedded || (event.pointerType === "mouse" && event.button !== 0)) return
    dragStartYRef.current = event.clientY
    setSheetDragOffset(0)
    setIsDragging(true)
    event.currentTarget.setPointerCapture(event.pointerId)
  }

  const handleGrabberPointerMove = (event: ReactPointerEvent<HTMLElement>) => {
    if (!embedded || dragStartYRef.current === null) return
    event.preventDefault()
    setSheetDragOffset(event.clientY - dragStartYRef.current)
  }

  const finishGrabberDrag = (event: ReactPointerEvent<HTMLElement>, allowClose: boolean) => {
    if (!embedded) return
    const shouldClose = allowClose && dragOffsetRef.current >= 72
    dragStartYRef.current = null
    setSheetDragOffset(0)
    setIsDragging(false)
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId)
    }
    if (shouldClose) close()
  }

  useEffect(() => {
    const code = extractInviteCode(startParam)
    if (code) setJoinCode(code)
  }, [startParam])

  useEffect(() => {
    const code = joinCode.trim()
    if (!code || code.length < 8) {
      setInvitePreview(null)
      return
    }

    let cancelled = false
    setIsInvitePreviewLoading(true)
    autoclanApi.invitePreview(code)
      .then((preview) => {
        if (!cancelled) setInvitePreview(preview)
      })
      .catch(() => {
        if (!cancelled) setInvitePreview(null)
      })
      .finally(() => {
        if (!cancelled) setIsInvitePreviewLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [joinCode])

  useEffect(() => {
    if (selectedClanId) {
      if (detail?.id !== selectedClanId) void loadDetail(selectedClanId)
    } else {
      setDetail(null)
    }
  }, [selectedClanId])

  const loadClans = async () => {
    setIsLoading(true)
    try {
      await refreshAutoclanState()
      const state = useAuthStore.getState()
      setClans(state.autoclans)
      setDetail(state.autoclanDetail)
      setSelectedClanId(state.autoclanDetail?.id ?? state.autoclans[0]?.id ?? null)
    } finally {
      setIsLoading(false)
    }
  }

  const loadDetail = async (clanId: number) => {
    setIsDetailLoading(true)
    try {
      const data = await autoclanApi.detail(clanId)
      setDetail(data)
      useAuthStore.setState({ autoclanDetail: data, autoclanReady: true })
    } catch (err: any) {
      showAlert(runtimeError(err, "loadError"))
    } finally {
      setIsDetailLoading(false)
    }
  }

  const handleCreate = async () => {
    const normalized = newClanName.trim()
    if (normalized.length < 4) {
      showAlert(c("nameLength"))
      return
    }
    hapticImpact("medium")
    setIsCreating(true)
    try {
      const created = await autoclanApi.create(normalized)
      hapticNotification("success")
      setNewClanName("")
      await loadClans()
      setSelectedClanId(created.id)
      setNotice("created")
      window.setTimeout(() => setNotice(null), 3000)
    } catch (err: any) {
      hapticNotification("error")
      showAlert(runtimeError(err, "createError"))
    } finally {
      setIsCreating(false)
    }
  }

  const handleJoin = async () => {
    const code = joinCode.trim()
    if (!code) return

    if (invitePreview?.already_in_other_clan) {
      showAlert(locale === "ru" && invitePreview.blocked_by_other_clan_message ? invitePreview.blocked_by_other_clan_message : c("alreadyOtherClan"))
      return
    }

    if (invitePreview && !invitePreview.has_active_garage) {
      showAlert(c("garageRequired"))
      return
    }

    hapticImpact("medium")
    setIsJoining(true)
    try {
      const result = await autoclanApi.join(code)
      hapticNotification("success")
      showAlert(c("joined", { name: result.clan_name }))
      setJoinCode("")
      setInvitePreview(null)
      await loadClans()
      setSelectedClanId(result.clan_id)
    } catch (err: any) {
      hapticNotification("error")
      showAlert(runtimeError(err, "joinError"))
    } finally {
      setIsJoining(false)
    }
  }

  const handleInvite = async (clan: Autoclan | AutoclanDetail) => {
    hapticImpact("light")
    try {
      const invite = await autoclanApi.createInvite(clan.id)
      const clanUrl = `https://t.me/${import.meta.env.VITE_BOT_USERNAME || "BeepAppBot"}?startapp=clan_${invite.invite_code}`
      const chatLine = invite.chat_url
        ? `\n${locale === "ru" ? "Чат клана" : "Clan chat"}: ${invite.chat_url}`
        : ""
      shareUrl(
        clanUrl,
        `${c("inviteText", { name: clan.name })}${chatLine}`
      )
    } catch (err: any) {
      showAlert(runtimeError(err, "inviteError"))
    }
  }

  const openClanChatUrl = (url: string) => {
    if (tg?.openTelegramLink) tg.openTelegramLink(url)
    else window.open(url, "_blank", "noopener,noreferrer")
  }

  const openChatEditor = (initialUrl = "") => {
    setChatUrlInput(initialUrl)
    setIsChatEditorOpen(true)
  }

  const handleChat = async () => {
    if (!detail) return
    hapticImpact("light")

    if (!detail.telegram_chat_linked) {
      if (detail.current_user_role === "owner") {
        openChatEditor(detail.telegram_chat_public_url || "")
      } else {
        showAlert(chatCopy.notReady)
      }
      return
    }

    try {
      const state = await autoclanApi.getChat(detail.id)
      if (state.linked && state.public_url) {
        setDetail((current) => current ? {
          ...current,
          telegram_chat_linked: true,
          telegram_chat_title: state.title,
          telegram_chat_public_url: state.public_url,
        } : current)
        openClanChatUrl(state.public_url)
        return
      }

      if (detail.current_user_role === "owner") {
        openChatEditor(state.public_url || detail.telegram_chat_public_url || "")
      } else {
        showAlert(chatCopy.unavailable)
      }
    } catch (err: any) {
      showAlert(locale === "ru" && err?.message ? err.message : chatCopy.openError)
    }
  }

  const handleSaveChat = async () => {
    if (!detail || detail.current_user_role !== "owner" || isSavingChat) return
    const publicUrl = chatUrlInput.trim()
    if (!isValidPublicTelegramChatLink(publicUrl)) return

    setIsSavingChat(true)
    hapticImpact("medium")
    try {
      const state = await autoclanApi.linkChat(detail.id, publicUrl)
      const updatedDetail: AutoclanDetail = {
        ...detail,
        telegram_chat_linked: state.linked,
        telegram_chat_title: state.title,
        telegram_chat_public_url: state.public_url,
      }
      setDetail(updatedDetail)
      useAuthStore.setState({ autoclanDetail: updatedDetail, autoclanReady: true })
      setIsChatEditorOpen(false)
      setChatUrlInput("")
      hapticNotification("success")
    } catch (err: any) {
      hapticNotification("error")
      showAlert(locale === "ru" && err?.message ? err.message : chatCopy.saveError)
    } finally {
      setIsSavingChat(false)
    }
  }

  const handleLeave = async (clan: AutoclanDetail) => {
    const ok = await showConfirm(c("leaveConfirm", { name: clan.name }))
    if (!ok) return
    try {
      await autoclanApi.leave(clan.id)
      hapticNotification("success")
      setSelectedClanId(null)
      await loadClans()
      setNotice("left")
      window.setTimeout(() => {
        setNotice(null)
        close()
      }, 3000)
    } catch (err: any) {
      hapticNotification("error")
      showAlert(runtimeError(err, "leaveError"))
    }
  }

  const handleRemoveMember = async (member: AutoclanMember) => {
    if (!detail) return
    const label = member.plate_display || member.first_name || `ID ${member.user_id}`
    const ok = await showConfirm(c("removeConfirm", { member: label, name: detail.name }))
    if (!ok) return
    try {
      await autoclanApi.removeMember(detail.id, member.id)
      hapticNotification("success")
      await loadDetail(detail.id)
    } catch (err: any) {
      hapticNotification("error")
      showAlert(runtimeError(err, "removeError"))
    }
  }

  const handleRequestTransfer = async (member: AutoclanMember) => {
    if (!detail) return
    const label = member.plate_display || member.first_name || `ID ${member.user_id}`
    const ok = await showConfirm(c("transferConfirm", { member: label, name: detail.name }))
    if (!ok) return
    try {
      await autoclanApi.requestOwnershipTransfer(detail.id, member.id)
      hapticNotification("success")
      showAlert(c("transferSent"))
      await loadDetail(detail.id)
    } catch (err: any) {
      hapticNotification("error")
      showAlert(runtimeError(err, "transferError"))
    }
  }

  const handleAcceptTransfer = async () => {
    if (!detail?.pending_transfer) return
    try {
      await autoclanApi.acceptOwnershipTransfer(detail.id, detail.pending_transfer.id)
      hapticNotification("success")
      showAlert(c("becameOwner"))
      await loadClans()
      await loadDetail(detail.id)
    } catch (err: any) {
      hapticNotification("error")
      showAlert(runtimeError(err, "acceptTransferError"))
    }
  }

  const handleDeclineTransfer = async () => {
    if (!detail?.pending_transfer) return
    try {
      await autoclanApi.declineOwnershipTransfer(detail.id, detail.pending_transfer.id)
      hapticNotification("success")
      await loadDetail(detail.id)
    } catch (err: any) {
      hapticNotification("error")
      showAlert(runtimeError(err, "declineTransferError"))
    }
  }

  const handleDeleteClan = async (clan: AutoclanDetail) => {
    const ok = await showConfirm(c("deleteConfirm", { name: clan.name }))
    if (!ok) return
    try {
      await autoclanApi.deleteClan(clan.id)
      hapticNotification("success")
      setSelectedClanId(null)
      await loadClans()
      setNotice("left")
      window.setTimeout(() => {
        setNotice(null)
        close()
      }, 3000)
    } catch (err: any) {
      hapticNotification("error")
      showAlert(runtimeError(err, "deleteError"))
    }
  }

  void handleRequestTransfer

  if (!embedded && isPreviewVehicleLoading && !previewVehicle) return <div className="bbf-garage-loading" aria-label={c("loading")} />

  const signSizingText = displayedClanName || newClanName || c("genericName")
  const membersLoading = isLoading || isDetailLoading || (isInvitePreviewMode && isInvitePreviewLoading)

  const sheet = (
      <section
        className={`bbf-autoclan-sheet ${detail || isInvitePreviewMode ? "state-after-create" : "state-before-create"}${embedded ? " is-embedded" : ""}${embedded && isDragging ? " is-dragging" : ""}`}
        style={embedded ? ({ "--bbf-autoclan-drag-y": `${dragOffset}px` } as CSSProperties) : undefined}
        onClick={(event) => event.stopPropagation()}
      >
        <i
          className="bbf-autoclan-grabber"
          onPointerDown={embedded ? handleGrabberPointerDown : undefined}
          onPointerMove={embedded ? handleGrabberPointerMove : undefined}
          onPointerUp={embedded ? (event) => finishGrabberDrag(event, true) : undefined}
          onPointerCancel={embedded ? (event) => finishGrabberDrag(event, false) : undefined}
        />
        <h1 dir={direction}>{c("title")}</h1>
        <div className="bbf-autoclan-sign">
          <span className="bbf-autoclan-sign-sizer" aria-hidden="true">{signSizingText}</span>
          {detail || isInvitePreviewMode
            ? <span className="bbf-autoclan-sign-value">{displayedClanName || c("genericName")}</span>
            : <input className="bbf-autoclan-sign-value bbf-autoclan-sign-input" aria-label={c("nameAria")} placeholder={c("genericName")} value={newClanName} onChange={(event) => { const next = normalizeClanNameInput(event.target.value); if (CLAN_NAME_RE.test(next)) setNewClanName(next) }} />}
        </div>
        <div className="bbf-autoclan-members">
          {membersLoading ? <p dir={direction}>{c("loading")}</p> : displayedMembers.map((member) => (
            <button
              key={member.id}
              className={`bbf-autoclan-member ${member.urgent_state}`}
              title={`${member.role === "owner" ? c("owner") : c("member")}: ${member.plate_display || member.first_name}`}
              onClick={() => {
                if (detail?.current_user_role === "owner" && member.role !== "owner") void handleRemoveMember(member)
              }}
            >{member.username ? `@${member.username}` : member.plate_display || member.first_name}</button>
          ))}
        </div>
        {detail?.pending_transfer?.is_for_current_user && (
          <div className="bbf-autoclan-transfer"><span dir={direction}>{c("acceptOwner")}</span><button onClick={handleAcceptTransfer} dir={direction}>{c("yes")}</button><button onClick={handleDeclineTransfer} dir={direction}>{c("no")}</button></div>
        )}
        <button className="bbf-autoclan-affiliate" onClick={() => detail && void handleInvite(detail)}>
          <span className="bbf-autoclan-affiliate-icon" style={autoclanMaskStyle(affiliateIcon)} aria-hidden="true" />
          <span className="bbf-autoclan-affiliate-copy" dir={direction}>{c("affiliate1")}<br />{c("affiliate2")}<br />{c("affiliate3")}</span>
        </button>
        {detail ? (
          <>
            <button
              className="bbf-autoclan-leave"
              onClick={() => void (detail.current_user_role === "owner" ? handleDeleteClan(detail) : handleLeave(detail))}
              dir={direction}
            >{c("leaveClan")}</button>
            <button
              className="bbf-autoclan-invite-action"
              onClick={() => void handleInvite(detail)}
              dir={direction}
            >{chatCopy.invite}</button>
          </>
        ) : isInvitePreviewMode ? (
          <button
            className="bbf-autoclan-create"
            disabled={inviteJoinDisabled}
            onClick={() => void handleJoin()}
            dir={direction}
          >{inviteJoinLabel}</button>
        ) : (
          <button
            className="bbf-autoclan-create"
            disabled={isCreating}
            onClick={() => void handleCreate()}
            dir={direction}
          >{isCreating ? c("creating") : c("createClan")}</button>
        )}
        <button className="bbf-autoclan-back" onClick={close} dir={direction}>{t("common.back")}</button>
        <button
          className="bbf-autoclan-chat-action"
          disabled={!detail}
          onClick={() => detail && void handleChat()}
          dir={direction}
        >{chatCopy.chat}</button>
        {isChatEditorOpen && detail?.current_user_role === "owner" && (
          <div className="bbf-autoclan-chat-editor" onClick={(event) => event.stopPropagation()}>
            <i className="bbf-autoclan-chat-grabber" />
            <h2 dir={direction}>{chatCopy.title}</h2>
            <input
              className={chatUrlIsInvalid ? "is-invalid" : ""}
              type="url"
              inputMode="url"
              autoCapitalize="none"
              autoCorrect="off"
              spellCheck={false}
              aria-invalid={chatUrlIsInvalid}
              value={chatUrlInput}
              placeholder={chatCopy.placeholder}
              onChange={(event) => setChatUrlInput(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === "Enter") {
                  event.preventDefault()
                  void handleSaveChat()
                }
              }}
            />
            <div className="bbf-autoclan-chat-editor-actions">
              <button type="button" onClick={() => setIsChatEditorOpen(false)} dir={direction}>{t("common.back")}</button>
              <button type="button" disabled={isSavingChat || !chatUrlIsValid} onClick={() => void handleSaveChat()} dir={direction}>
                {chatCopy.save}
              </button>
            </div>
          </div>
        )}
      </section>
  )

  if (embedded) {
    return (
      <div className={`bbf-autoclan-overlay ${isIOS ? "platform-ios" : "platform-android"}`} onClick={close}>
        {notice && <EmbeddedAutoclanNotice notice={notice} isIOS={isIOS} />}
        {notice !== "left" && sheet}
      </div>
    )
  }

  if (!previewVehicle) {
    return <div className="bbf-garage-loading" aria-label={c("loading")} />
  }

  return (
    <GarageBackdrop vehicle={previewVehicle} isIOS={isIOS} active onBack={close}>
      <div className={`bbf-autoclan-overlay ${isIOS ? "platform-ios" : "platform-android"}`} onClick={close}>
        {notice && <EmbeddedAutoclanNotice notice={notice} isIOS={isIOS} />}
        {notice !== "left" && sheet}
      </div>
    </GarageBackdrop>
  )
}
